import env from 'env'

import { BaseSDK } from '../sdk'

import { EVENT_PLAYBACK_STATUS, VIDEO_PLAYER_TYPE } from '../constants/enums'
import { SDK_CONCURRENT_PLAYBACK_START, SDK_CONCURRENT_PLAYBACK_STOP } from '../events/types/sdk.events'
import {
  VIDEO_PLAYBACK_CHAPTER_END,
  VIDEO_PLAYBACK_CHAPTER_START,
  VIDEO_PLAYBACK_ENDED,
  VIDEO_PLAYBACK_ERROR,
  VIDEO_PLAYBACK_INITIATED,
  VIDEO_PLAYBACK_PAUSE,
  VIDEO_PLAYBACK_SEEK,
  VIDEO_PLAYBACK_START,
  VIDEO_PLAYBACK_TIME
} from '../events/types/video.events'

import { Config, PlayerSrc, VendorVideoPlayer } from './players'

import { NugsJWPlayer } from './players/jwplayer'
import { NugsBitmovinPlayer } from './players/bitmovin'
import { load, logger } from '../utils'
import { EventsTrait } from '../events/bus'
import { Release, ReleaseProduct } from '../components/release'
import { SDKCustomEvent } from '../events'

import createTelemetrySession from '../services/telemetry/session'
import { useToken } from '../services/api'
import { VideoPlaybackTelemetry } from '../services/telemetry/video'
import { Chapter, SourceData } from '../services/telemetry/types'
import { ChapterData, ChapterProgressData } from '../services/telemetry/video/events'

import { AmplitudeAnalytics, CONTENT_TYPE, STOP_REASON } from '../services/analytics/amplitude'

import { ACCESS_TYPE } from '../services/telemetry/enums'

class VideoPlayer extends EventsTrait {
  sdk_filename = 'nugsnet.websdk.esm.min.mjs'

  #type: VIDEO_PLAYER_TYPE
  #config: Config
  #sdk?: BaseSDK
  #userId: string
  #videoPlayer: NugsJWPlayer | NugsBitmovinPlayer
  #showId?: string | number
  #release!: Release<ReleaseProduct>
  #source!: SourceData

  get events() {
    return this.player.events
  }

  set src(value: PlayerSrc) {
    this.player.src = value
  }

  get chapters() {
    return this.player.chapters.list
  }

  set chapters(value: Array<Chapter>) {
    this.player.chapters = value
  }

  get sdk() {
    return this.#sdk
  }

  get config() {
    return this.#config
  }

  set showId(value: string) {
    this.#showId = value
  }

  get release() {
    return this.#release
  }

  set release(value: Release<ReleaseProduct>) {
    this.#release = value
  }

  set source(value: SourceData) {
    this.#source = value
  }

  get source(): SourceData {
    return this.#source
  }

  get telemetry(): VideoPlaybackTelemetry {
    return (this.sdk?.telemetry?.video || {}) as VideoPlaybackTelemetry
  }

  get amplitude() {
    return (this.sdk?.amplitude || {}) as AmplitudeAnalytics
  }

  constructor(type: VIDEO_PLAYER_TYPE, config: Config, userId = '', sdk?: BaseSDK) {
    super()
    this.#type = type
    this.#config = { mode: 'mixed', ...config }
    this.#sdk = sdk
    this.#userId = userId

    switch (this.#type) {
      case 'jwplayer': {
        this.#videoPlayer = new NugsJWPlayer(this.#config, this.#userId)
        break
      }
      case 'bitmovin': {
        this.#videoPlayer = new NugsBitmovinPlayer(this.#config, this.#userId)
        break
      }
      default: {
        throw new Error(`Player "${ this.#type }" is not supported.`)
      }
    }
  }

  get player(): VendorVideoPlayer<PlayerSrc> {
    return this.#videoPlayer
  }

  async #loadSDK() {
    if (this.sdk) return { sdk: this.sdk }
    const version = this.config.sdk.version ? this.config.sdk.version : env.VERSION

    return new Promise<{ sdk: BaseSDK }>(resolve => {
      document.addEventListener('nugsnet:sdk:loaded', ((event: CustomEvent) => resolve(event.detail)) as EventListener)

      const path = env.LOCAL ? '../dist/nugsnet.websdk.esm.min.mjs' : `${ env.NUGSNET_CDN }/websdk/${ version }/${ this.sdk_filename }`
      load<BaseSDK>(path, undefined, 'module')
    })
  }

  public async load<T>() {
    if (!this.sdk) {
      const [{ sdk }] = await Promise.all([
        this.#loadSDK(),
        this.player.load<T>()
      ])

      this.#sdk = sdk
    } else {
      await this.player.load<T>()
    }

    if (this.sdk) Release.sdk = this.sdk

    await this.sdk?.configure(this.config.sdk.applicationId, 'Nugs Video Player', 'login')

    useToken(this.sdk?.account.tokens.token || '')

    if (this.#showId && !this.#release) this.#release = await Release.fetch(this.#showId)

    this.player.chapters = this.#release.chapters
    this.telemetry.emitSendStorage()
    const accessType = this.telemetry?.session?.accessType || ACCESS_TYPE.SUBSCRIPTION

    const onPlaybackStart = () => {
      this.sdk?.events.dispatchEvent(SDK_CONCURRENT_PLAYBACK_START)
      this.telemetry.playbackStarted()
    }

    const onPlaybackInitiated = () => {
      this.sdk?.events.dispatchEvent(SDK_CONCURRENT_PLAYBACK_START)

      if (this.sdk && this.release?.id && this.sdk.telemetry) {
        try {
          this.telemetry.add(createTelemetrySession(this.sdk, this.release.data, this.source))
        } catch (e) {
          logger.ERROR(e)
        }

        this.telemetry.emitInitiated()
        this.telemetry.playbackChapterStarted(this.player.chapters.current as unknown as ChapterProgressData)

        this.amplitude?.logVideoPlaybackInit(this.release.data, this.source)
      }
    }

    const onPlaybackPause = () => this.telemetry.playbackPaused()

    const onPlaybackEnded = () => {

      const duration = this.telemetry?.playbackDuration || 0

      this.telemetry.playbackEnded(EVENT_PLAYBACK_STATUS.COMPLETED)

      if (!this.release?.chapters || this.release?.chapters?.length === 0) {
        this.amplitude.logPlaybackEnded(CONTENT_TYPE.VIDEO, this.release, this.source, accessType, duration, STOP_REASON.TRACK_END)
      }
    }

    const onPlaybackError = () => logger.ERROR('Video playback error')

    const onPlaybackSeek = (event: SDKCustomEvent<{ time: number, destinationTime: number }>) =>
      this.telemetry.playbackSeek(event.detail.time, event.detail.destinationTime)

    const onPlaybackChapterStart = (event: SDKCustomEvent<{ chapter: ChapterData }>) =>
      this.telemetry.playbackChapterStarted(event.detail.chapter)

    const onPlaybackChapterEnded = (event: SDKCustomEvent<{ previousChapter: ChapterData, chapter: ChapterData, reason: EVENT_PLAYBACK_STATUS }>) => {
      this.telemetry.playbackChapterEnded(event.detail.previousChapter, event.detail.chapter, event.detail.reason)

      const stopReason = event.detail.reason == EVENT_PLAYBACK_STATUS.SKIPPED ? STOP_REASON.TRACK_SKIP : STOP_REASON.TRACK_END

      if (event.detail.reason != EVENT_PLAYBACK_STATUS.PENDING)
        this.amplitude.logPlaybackEnded(CONTENT_TYPE.VIDEO, this.release, this.source, accessType, event.detail.previousChapter.duration, stopReason, event.detail.previousChapter.title)
    }

    const onPlaybackTime = (event: SDKCustomEvent<{ time: number, ranges: TimeRanges, duration: number }>) =>
      this.telemetry.playbackTime(event.detail.time, event.detail.ranges, event.detail.duration)

    this.player.on(VIDEO_PLAYBACK_START, onPlaybackStart)
    this.player.on(VIDEO_PLAYBACK_INITIATED, onPlaybackInitiated)
    this.player.on(VIDEO_PLAYBACK_PAUSE, onPlaybackPause)
    this.player.on(VIDEO_PLAYBACK_ENDED, onPlaybackEnded)
    this.player.on(VIDEO_PLAYBACK_ERROR, onPlaybackError)
    this.player.on(VIDEO_PLAYBACK_SEEK.type, onPlaybackSeek)
    this.player.on(VIDEO_PLAYBACK_CHAPTER_START.type, onPlaybackChapterStart)
    this.player.on(VIDEO_PLAYBACK_CHAPTER_END.type, onPlaybackChapterEnded)
    this.player.on(VIDEO_PLAYBACK_TIME.type, onPlaybackTime)

    this.sdk?.events.addEventListener(SDK_CONCURRENT_PLAYBACK_STOP, () => this.player.pause())
  }

  public async mount(el?: HTMLElement | HTMLVideoElement) {
    return await this.player.mount(el)
  }

  public async unmount() {
    if (this.telemetry && !this.telemetry.isComplete) {
      const type = this.telemetry?.session?.accessType || ACCESS_TYPE.SUBSCRIPTION
      const duration = this.telemetry?.playbackDuration || 0

      if (!this.release?.chapters || this.release?.chapters?.length === 0) {
        this.amplitude.logPlaybackEnded(CONTENT_TYPE.VIDEO, this.release, this.source, type, duration, STOP_REASON.TRACK_END)
      }

      this.telemetry.playbackEnded(EVENT_PLAYBACK_STATUS.CLOSED)
    }
    return await this.player.unmount()
  }

  public init() {
    if (this.sdk?.isAuthorized) {
      return this.player.init()
    }
  }

  public stop() {
    this.player.stop()
  }

  public play() {
    this.player.play()
  }

  public pause() {
    this.player.pause()
  }

  public seek(time: number) {
    this.player.seek(time)
  }

  public useRelease(value: ReleaseProduct) {
    this.#release = Release.use(value)
  }
}

export { VideoPlayer }

